import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const KothContent: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Tournaly now supports six bracket formats — and the newest one is unlike any other.
      King of the Hill is a gauntlet-style tournament format where one player sits on the
      throne and every challenger must defeat them to take the crown. It's dramatic, it's
      tense, and it rewards consistency over luck.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      What is King of the Hill?
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      King of the Hill — also known as "winner stays on" or "last man standing" — is one
      of the oldest competitive formats in gaming and sports. Unlike elimination brackets
      where matches happen simultaneously across multiple pairs, King of the Hill is a
      sequential gauntlet: every match is a direct challenge to the current champion.
      There is only ever one match happening at a time, and everyone is watching.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      This makes it ideal for social settings where spectating is as important as playing —
      fighting game nights, billiards sessions, table tennis tournaments, or any game where
      the crowd wants to follow a single storyline.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      How it works
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      The top seed starts as the King. Challengers enter in order of their seed — seed 2
      faces the King first. The winner stays on the throne and faces the next challenger.
      This continues until all challengers have had their shot. The player who holds the
      throne at the end wins the tournament.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      A King of the Hill tournament with N players produces exactly N−1 matches. This is
      notably fewer than a Round Robin (N×(N−1)/2 matches) and slightly fewer than double
      elimination, making it fast to run even with large groups.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Scoring and standings
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      The leaderboard ranks players by how far they progressed in the gauntlet — specifically,
      by which round they were eliminated. A player who held the throne for five consecutive
      defenses before losing ranks above someone who lost in round two, even if both players
      lost exactly once. The final King is always ranked first.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      This creates a clear, satisfying hierarchy that reflects actual head-to-head performance
      rather than seeding or luck of the draw.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Best use cases
    </Text>
    {[
      'Fighting game nights — Street Fighter, Tekken, Smash Bros sessions where winner stays on',
      'Pool or billiards tournaments where everyone wants a shot at the champion',
      'Table tennis, ping pong, or foosball round-robins where simultaneous play is impossible',
      'Esports scrimmage sessions where coaching between matches adds strategic depth',
      'Any group where spectating matters as much as playing — one match, full attention',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Tips for organizers
    </Text>
    {[
      "Seed carefully — the King starts with the advantage of knowing every challenger's style",
      'Consider random seeding for a more chaotic and entertaining session',
      'Set a time limit per match to keep the night moving, especially with large groups',
      'Use a best-of-3 format for matches to reduce variance and reward skill over luck',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      How to run King of the Hill in Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Creating a King of the Hill tournament in Tournaly takes under a minute. Select
      "King of the Hill" as the format when creating a tournament, add your participants,
      and tap Generate Bracket. Tournaly automatically chains all matches in gauntlet order,
      tracks the current king, and updates the leaderboard in real time as results come in.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Pro-only format
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      King of the Hill is available exclusively on Tournaly Pro, alongside Double Elimination
      and Swiss formats. The free plan includes Single Elimination and Round Robin — both
      great starting points for any tournament organizer.
    </Text>
  </View>
);

const KothContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Tournaly soporta ahora seis formatos de bracket — y el más nuevo no se parece a ningún otro.
      King of the Hill es un formato tipo gauntlet donde un jugador ocupa el trono y cada
      desafiante debe derrotarle para quitarle la corona. Es dramático, tenso, y recompensa
      la consistencia por encima de la suerte.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      ¿Qué es King of the Hill?
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      King of the Hill — también conocido como "el ganador se queda" o "último en pie" — es uno
      de los formatos competitivos más antiguos en el gaming y el deporte. A diferencia de los
      brackets de eliminación donde los partidos ocurren simultáneamente en varios emparejamientos,
      King of the Hill es un gauntlet secuencial: cada partido es un desafío directo al campeón
      actual. Solo hay un partido en cada momento, y todos están mirando.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Esto lo hace ideal para entornos sociales donde ver jugar importa tanto como jugar —
      noches de juegos de lucha, sesiones de billar, torneos de tenis de mesa, o cualquier
      juego donde el público quiere seguir una sola historia.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Cómo funciona
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      El cabeza de serie 1 empieza como Rey. Los desafiantes entran en orden de cabeza de
      serie — el 2 se enfrenta al Rey primero. El ganador permanece en el trono y se enfrenta
      al siguiente desafiante. Esto continúa hasta que todos los desafiantes han tenido su
      oportunidad. El jugador que ocupe el trono al final gana el torneo.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Un torneo King of the Hill con N jugadores produce exactamente N−1 partidos. Esto es
      notablemente menos que un Round Robin (N×(N−1)/2 partidos) y ligeramente menos que
      la doble eliminación, lo que lo hace rápido incluso con grupos grandes.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Puntuación y clasificación
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      El marcador clasifica a los jugadores por lo lejos que llegaron en el gauntlet —
      específicamente, por la ronda en que fueron eliminados. Un jugador que defendió el
      trono cinco veces antes de perder queda por encima de alguien que perdió en la ronda
      dos, aunque ambos hayan perdido exactamente una vez. El Rey final siempre ocupa el primer puesto.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Esto crea una jerarquía clara y satisfactoria que refleja el rendimiento real cara a cara
      en lugar del sorteo o la suerte.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Mejores casos de uso
    </Text>
    {[
      'Noches de juegos de lucha — Street Fighter, Tekken, Smash Bros donde el ganador se queda',
      'Torneos de billar o pool donde todos quieren retar al campeón',
      'Tenis de mesa, ping pong o futbolín donde el juego simultáneo es imposible',
      'Sesiones de esports donde el entrenamiento entre partidos añade profundidad estratégica',
      'Cualquier grupo donde ver importa tanto como jugar — un partido, atención total',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Consejos para organizadores
    </Text>
    {[
      'Siembra con cuidado — el Rey empieza con la ventaja de conocer el estilo de cada desafiante',
      'Considera la siembra aleatoria para una sesión más caótica y entretenida',
      'Establece un límite de tiempo por partido para mantener el ritmo, especialmente con grupos grandes',
      'Usa el formato al mejor de 3 para reducir la varianza y recompensar la habilidad sobre la suerte',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Cómo ejecutar King of the Hill en Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Crear un torneo King of the Hill en Tournaly lleva menos de un minuto. Selecciona
      "King of the Hill" como formato al crear el torneo, añade tus participantes y pulsa
      Generar Bracket. Tournaly encadena automáticamente todos los partidos en orden de
      gauntlet, rastrea al rey actual y actualiza el marcador en tiempo real conforme
      llegan los resultados.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Formato exclusivo Pro
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      King of the Hill está disponible exclusivamente en Tournaly Pro, junto con los formatos
      de Doble Eliminación y Suizo. El plan gratuito incluye Eliminación Simple y Round Robin —
      ambos excelentes puntos de partida para cualquier organizador de torneos.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-03-03-king-of-the-hill',
  title: 'New Format: King of the Hill',
  titleEs: 'Nuevo Formato: King of the Hill',
  date: '2026-03-03',
  summary: 'King of the Hill tournament format explained — the classic winner-stays-on gauntlet where challengers try to dethrone the king one by one. Available on Tournaly Pro.',
  summaryEs: 'El formato de torneo King of the Hill explicado — el clásico gauntlet donde el ganador se queda y los desafiantes intentan derrocar al rey uno a uno. Disponible en Tournaly Pro.',
  tags: ['Formats', 'Feature', 'Pro'],
  Content: KothContent,
  ContentEs: KothContentEs,
};
